import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Section, Container, Eyebrow, DisplayH1 } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock } from "lucide-react";

const SPECIALTIES = ["Puppy", "Behavior", "Reactivity", "Aggression", "Service Dogs", "Working Dogs", "Detection", "Protection", "Rescue", "Business", "Youth"];

export default function AlumniJoin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    display_name: "", headline: "", bio: "", city: "", country: "",
    program: "", year_certified: "", photo_url: "", website: "", email_public: "",
    specialties: [], accepts_clients: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/alumni/me/profile").then((r) => {
      if (r.data && r.data.headline) setForm((f) => ({ ...f, ...r.data, year_certified: r.data.year_certified || "" }));
      else setForm((f) => ({ ...f, display_name: user.name || "" }));
    });
  }, [user]);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const toggleSpec = (s) => setForm({
    ...form,
    specialties: form.specialties.includes(s) ? form.specialties.filter((x) => x !== s) : [...form.specialties, s],
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, year_certified: form.year_certified ? Number(form.year_certified) : null };
      // strip empty optional email so EmailStr doesn't reject empty string
      if (!payload.email_public) delete payload.email_public;
      const r = await api.post("/alumni/opt-in", payload);
      toast.success("Your profile is live in the directory.");
      nav(`/alumni/${r.data.alumni_id}`);
    } catch (e) {
      toast.error("Could not save your profile.");
    } finally { setBusy(false); }
  };

  const optOut = async () => {
    if (!window.confirm("Remove your profile from the public directory?")) return;
    try { await api.delete("/alumni/me"); toast.success("Removed from directory."); nav("/alumni"); }
    catch { toast.error("Could not remove your profile."); }
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-3 text-lg";

  if (loading) return <Section className="pt-40 pb-40"><Container><div>Loading…</div></Container></Section>;
  if (!user) return (
    <Section className="pt-40 pb-40"><Container>
      <Lock className="text-[color:var(--wk-gold)]" />
      <Eyebrow className="mt-4">Alumni-only</Eyebrow>
      <DisplayH1 className="mt-6 max-w-2xl">Sign in to opt into the directory.</DisplayH1>
      <div className="mt-8"><Link to="/login" className="btn-gold">Sign in</Link></div>
    </Container></Section>
  );

  return (
    <Section testid="alumni-join" className="pt-40 pb-32">
      <Container>
        <Eyebrow>Alumni Directory</Eyebrow>
        <DisplayH1 className="mt-4">Publish your profile.</DisplayH1>
        <p className="mt-4 max-w-2xl text-[color:var(--wk-bone-dim)] leading-relaxed">
          Families and dogs search this directory to find certified Wolfkeeper trainers. Complete
          your profile below — you can update or remove it at any time.
        </p>
        <form onSubmit={submit} data-testid="alumni-join-form" className="mt-10 grid gap-8 max-w-4xl">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="overline">Display Name</label>
              <input required value={form.display_name} onChange={(e) => set("display_name", e.target.value)} className={inputCls} data-testid="join-display-name" />
            </div>
            <div>
              <label className="overline">Headline (60 chars ideal)</label>
              <input required value={form.headline} onChange={(e) => set("headline", e.target.value)} maxLength={140} className={inputCls} data-testid="join-headline" />
            </div>
            <div>
              <label className="overline">City</label>
              <input required value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid="join-city" />
            </div>
            <div>
              <label className="overline">Country</label>
              <input required value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} data-testid="join-country" />
            </div>
            <div>
              <label className="overline">Program (e.g. Behavior Academy · 2019)</label>
              <input value={form.program} onChange={(e) => set("program", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="overline">Year Certified</label>
              <input type="number" min="1990" max="2030" value={form.year_certified} onChange={(e) => set("year_certified", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="overline">Photo URL</label>
              <input value={form.photo_url} onChange={(e) => set("photo_url", e.target.value)} className={inputCls} placeholder="https://…" />
            </div>
            <div>
              <label className="overline">Website (optional)</label>
              <input value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} placeholder="https://…" />
            </div>
          </div>

          <div>
            <label className="overline">Bio</label>
            <textarea rows={4} value={form.bio} onChange={(e) => set("bio", e.target.value)} maxLength={1200} className={inputCls + " resize-none"} placeholder="Your story — training philosophy, background, notable placements." />
          </div>

          <div>
            <label className="overline">Specialties</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {SPECIALTIES.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => toggleSpec(s)}
                  data-testid={`join-spec-${s}`}
                  className={`px-3 py-1.5 rounded-full text-xs border ${form.specialties.includes(s) ? "bg-[color:var(--wk-gold)] text-black border-[color:var(--wk-gold)]" : "border-white/20 text-[color:var(--wk-bone-dim)] hover:border-[color:var(--wk-gold)] hover:text-[color:var(--wk-gold)]"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-3 text-sm text-[color:var(--wk-bone-dim)] cursor-pointer">
            <input type="checkbox" checked={form.accepts_clients} onChange={(e) => set("accepts_clients", e.target.checked)} className="accent-[color:var(--wk-gold)]" data-testid="join-accepts" />
            I am currently accepting new clients.
          </label>

          <div className="flex flex-wrap gap-3">
            <button disabled={busy} className="btn-gold" data-testid="join-submit">
              {busy ? "Publishing…" : "Publish my profile"}
            </button>
            <button type="button" onClick={optOut} className="btn-ghost">Remove from directory</button>
          </div>
        </form>
      </Container>
    </Section>
  );
}
